import {
  CalendarIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  InboxStackIcon,
} from '@heroicons/react/24/outline'
import moment from 'moment'
import { ReactElement, useState } from 'react'
import { SegmentedControl } from 'app/components/action-center/SegmentedControl'
import type { SegmentedOption } from 'app/components/action-center/SegmentedControl'
import { STATUS_META } from 'app/components/action-center/treatmentDisplay'
import { ActivityCard } from 'app/components/activities/ActivityCard'
import Spinner from 'app/components/Spinner'
import { Badge } from 'app/components/ui/badge'
import { Button } from 'app/components/ui/button'
import { useSettings } from 'app/hooks/useSettings'
import type { ProcedureGrouping, RawPlan } from 'app/types/treatment'
import { bg } from 'lib/background'
import { formatCurrency } from 'lib/formatCurrency'
import {
  anyPrioritized,
  DRAWER_GROUPING_SETTING,
  NOT_ON_A_PLAN,
  planGroups,
  priorityGroups,
  priorityLabel,
} from 'lib/procedureGrouping'
import type { TreatmentProcedure } from 'lib/treatmentCards'
import { groupBySection, TREATMENT_SECTIONS } from 'lib/treatmentSections'
import type { TreatmentSection } from 'lib/treatmentSections'

type Group = {
  key: string
  name: string
  icon: ReactElement
  description?: string
  procedures: TreatmentProcedure[]
  /** Starts folded: a closed plan's work is shown on request, never chased. */
  folded?: boolean
}

const NEUTRAL_ICON = `h-5 w-5 shrink-0 rounded-md bg-fill-neutral p-1 text-on-color`
const OUTLINE_ICON = `h-5 w-5 shrink-0 rounded-md border border-default bg-surface-raised p-1 text-subtle`

const OFF_PLAN_GROUP = {
  name: NOT_ON_A_PLAN,
  icon: <ClipboardDocumentListIcon className={OUTLINE_ICON} />,
  description: `Pending work no treatment plan carries, mostly routine hygiene.`,
}

/**
 * Order matters more than usual here. Someone opens this tab to decide whether
 * to pick up the phone, and the answer is nearly always in the first section:
 * work the patient already agreed to, on an appointment that then broke.
 */
const SECTIONS: Record<TreatmentSection, Omit<Group, `key` | `procedures`>> = {
  broken: {
    // The same four status words the Action Center's case dialog uses.
    name: `Broken`,
    icon: <ExclamationTriangleIcon className="h-5 w-5 shrink-0 rounded-md bg-orange-500 p-1 text-on-color" />,
    description: `They said yes, then the appointment fell through.`,
  },
  scheduled: {
    name: `Scheduled`,
    icon: <CalendarIcon className="h-5 w-5 shrink-0 rounded-md bg-blue-600 p-1 text-on-color" />,
    description: `Already booked in.`,
  },
  open: {
    name: `Unscheduled`,
    icon: <InboxStackIcon className={NEUTRAL_ICON} />,
    description: `Planned, never on an appointment that stuck.`,
  },
  unplanned: OFF_PLAN_GROUP,
}

const GROUPING_OPTIONS: SegmentedOption<ProcedureGrouping>[] = [
  { id: `status`, label: `Status`, title: `What became of each piece of work` },
  { id: `plan`, label: `Plan`, title: `Which treatment plan carries it` },
  { id: `priority`, label: `Priority`, title: `What the practice ranked first, in its own words` },
]

const DATE_FORMAT = `MMM D, YYYY`

function groupsFor(grouping: ProcedureGrouping, procedures: TreatmentProcedure[], plans: RawPlan[]): Group[] {
  if (grouping === `priority`) {
    return priorityGroups(procedures).map((group) => ({
      key: group.label,
      name: group.label,
      icon: <FlagIcon className={NEUTRAL_ICON} />,
      procedures: group.rows,
    }))
  }

  if (grouping === `plan`) {
    return planGroups(procedures, plans).map((group) => {
      if (group.id === `off-plan`) return { key: group.id, ...OFF_PLAN_GROUP, procedures: group.rows }
      const planned = group.created ? `Planned ${moment.utc(group.created).format(DATE_FORMAT)}.` : ``
      return {
        key: group.id,
        name: group.label,
        icon: <DocumentTextIcon className={group.dead ? OUTLINE_ICON : NEUTRAL_ICON} />,
        description: group.dead ? `${planned} Closed by the practice, outside the total.`.trim() : planned,
        procedures: group.rows,
        folded: group.dead,
      }
    })
  }

  const grouped = groupBySection(procedures)
  return TREATMENT_SECTIONS.filter((key) => (grouped.get(key) ?? []).length > 0).map((key) => ({
    key,
    ...SECTIONS[key],
    procedures: grouped.get(key) ?? [],
  }))
}

/** The chips say what the heading does not: status off the status view, priority off the priority view. */
function ProcedureRow({
  procedure,
  grouping,
}: {
  procedure: TreatmentProcedure
  grouping: ProcedureGrouping
}): ReactElement {
  const when = procedure.on ?? procedure.planned
  const priority = grouping === `priority` ? undefined : priorityLabel(procedure.priority)
  const status = grouping === `status` ? undefined : STATUS_META[procedure.status]

  return (
    <li className="flex flex-col gap-y-0.5 border-b border-subtle py-2 first:pt-0 last:border-b-0">
      <div className="flex items-baseline justify-between gap-x-3">
        <span className="flex min-w-0 flex-wrap items-center gap-1.5 text-ui-sm text-strong">
          <span className="truncate">
            {procedure.code}
            {procedure.tooth ? <span className="text-body-xs text-subtle">{` tooth ${procedure.tooth}`}</span> : null}
          </span>
          {status && <Badge color={status.badge} label={status.label} className="shrink-0" />}
          {priority && <Badge color="gray" label={priority} className="shrink-0" />}
        </span>
        <span className="shrink-0 text-ui-sm font-semibold text-strong">{formatCurrency(procedure.fee, 2)}</span>
      </div>
      <div className="flex items-baseline justify-between gap-x-3 text-body-xs text-subtle">
        {/* The practice's own wording, which is usually better than a code lookup. */}
        <span className="min-w-0 truncate">{procedure.description || `No description`}</span>
        <span className="shrink-0">{when ? moment.utc(when).format(DATE_FORMAT) : ``}</span>
      </div>
    </li>
  )
}

function GroupCard({ group, grouping }: { group: Group; grouping: ProcedureGrouping }): ReactElement {
  const [open, setOpen] = useState(!group.folded)
  const count = group.procedures.length

  return (
    <ActivityCard
      icon={group.icon}
      hideChevronIcon
      title={
        <div className="flex flex-wrap items-center gap-x-1.5 text-ui-sm text-strong">
          <span>{group.name}</span>
          {group.description && <span className="text-body-xs text-subtle">{group.description}</span>}
        </div>
      }
      preview={
        <div className="w-full rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm">
          {open && (
            <ul className="mb-2 flex flex-col">
              {group.procedures.map((procedure, index) => (
                <ProcedureRow
                  key={`${procedure.code}-${procedure.tooth}-${index}`}
                  procedure={procedure}
                  grouping={grouping}
                />
              ))}
            </ul>
          )}
          <div className="-mx-4 -mb-3 flex items-center justify-between gap-x-3 rounded-b-lg bg-surface-subtle px-4 py-2">
            <span className="flex items-center gap-x-2 text-body-xs text-subtle">
              {count} {count === 1 ? `procedure` : `procedures`}
              {group.folded && (
                <Button variant="link" size="inline" aria-expanded={open} onClick={() => setOpen((shown) => !shown)}>
                  {open ? `Hide` : `Show`}
                </Button>
              )}
            </span>
            <span className="shrink-0 text-ui-sm font-semibold text-strong">
              {formatCurrency(
                group.procedures.reduce((sum, item) => sum + item.fee, 0),
                2,
              )}
            </span>
          </div>
        </div>
      }
      isFirst
      isLast
    />
  )
}

function Empty({ children }: { children: React.ReactNode }): ReactElement {
  return <div className="px-4 py-10 text-center text-body-sm text-subtle">{children}</div>
}

/**
 * A contact's outstanding treatment, grouped by what happened to the work
 * unless the reader picks plan or priority.
 *
 * Status leads because it is the grouping that changes what you would do next.
 * Plans repeat it badly: every plan at a practice is often named "Active
 * Treatment Plan", and revisions point at the same live work, so a procedure
 * shows under each plan it is on.
 *
 * The headline is the list card's number: plan work on a plan the practice has
 * not closed and not on a booked appointment (TRK-6009).
 */
export function TreatmentWorkPanel({
  procedures,
  plans,
  fetching,
  hasError,
  hasPatientLink,
}: {
  procedures: TreatmentProcedure[]
  plans: RawPlan[]
  fetching: boolean
  hasError: boolean
  hasPatientLink: boolean
}): ReactElement {
  const [savedGrouping, setSavedGrouping] = useSettings<ProcedureGrouping>(DRAWER_GROUPING_SETTING)

  if (!hasPatientLink) return <Empty>This contact is not linked to a PMS patient.</Empty>

  if (hasError) {
    return (
      <div className="px-4 py-10 text-center text-body-sm">
        <ExclamationTriangleIcon className="mx-auto mb-2 h-6 w-6 text-warning-default" />
        <p className="text-default">Could not load treatment records.</p>
      </div>
    )
  }

  if (fetching && procedures.length === 0) {
    return (
      <div className="flex w-full items-center justify-center py-10">
        <Spinner size={20} />
      </div>
    )
  }

  if (procedures.length === 0) return <Empty>No outstanding treatment for this patient.</Empty>

  // Nobody ranked anything: Priority would be one pile, so it is not offered.
  const prioritized = anyPrioritized(procedures)
  const grouping: ProcedureGrouping =
    savedGrouping === `priority` && !prioritized ? `status` : (savedGrouping ?? `status`)
  const options = prioritized ? GROUPING_OPTIONS : GROUPING_OPTIONS.filter((option) => option.id !== `priority`)
  const unscheduled = procedures
    .filter((item) => !item.offPlan && !item.dead && (item.status === `open` || item.status === `broken`))
    .reduce((sum, item) => sum + item.fee, 0)

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 pb-3">
        <p className="text-body-sm text-subtle">
          <span className="text-ui-md font-semibold text-strong">{formatCurrency(unscheduled)}</span> unscheduled
        </p>
        <SegmentedControl
          options={options}
          value={grouping}
          onSelect={(next) => bg(setSavedGrouping(DRAWER_GROUPING_SETTING, next))}
        />
      </div>
      <ul className="flex flex-col gap-y-1.5 px-4">
        {groupsFor(grouping, procedures, plans).map((group) => (
          <li key={`${grouping}-${group.key}`}>
            <GroupCard group={group} grouping={grouping} />
          </li>
        ))}
      </ul>
    </>
  )
}
